import { Box } from '@mui/material';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import styles from './Dropzone.module.scss';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import DocumentIcon from 'assets/icons/document_48.svg?react';
import { useMemo } from 'react';
import clsx from 'clsx';

type AcceptFiles = '.docx' | '.dotx';

type DropzoneProps = Omit<DropzoneOptions, 'accept'> & {
  className?: string;
  accept?: Set<AcceptFiles>;
};

const acceptToMimeMap: Record<AcceptFiles, string> = {
  '.docx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.dotx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
};

export default function Dropzone(props: DropzoneProps) {
  const { className, accept, ...dropzoneOptions } = props;
  const i18n = useScopedTranslation('Component.TemplateFileUpload');
  const { getRootProps, getInputProps, isDragAccept, isDragReject } =
    useDropzone({
      ...dropzoneOptions,
      accept: accept
        ? Array.from(accept).reduce(
            (acc, ext) => {
              const mime = acceptToMimeMap[ext];
              if (mime) acc[mime] = [ext];
              return acc;
            },
            {} as Record<string, string[]>,
          )
        : undefined,
    });
  var dropStatus = useMemo(() => {
    return isDragAccept && !isDragReject
      ? 'active'
      : isDragReject && !isDragAccept
        ? 'reject'
        : null;
  }, [isDragAccept, isDragReject]);

  return (
    <Box
      {...getRootProps()}
      className={clsx(styles.dropzone, className)}
      drop-status={dropStatus}
    >
      <input {...getInputProps()} />

      <DocumentIcon />
      <p className={styles.attachMessage}>{i18n.t('attach_message')}</p>
      {accept && accept.size > 0 && (
        <p className={styles.supportedFormats}>
          {i18n.t('supported_formats', {
            supportedFormats: Array.from(accept).join(', '),
          })}
        </p>
      )}
    </Box>
  );
}
